import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Image, Tooltip } from 'antd';
import { Machine } from '../../../../../interfaces/Machine.types';
import { MachinePictureLoader } from '../../../../../components/MachinePictureLoader/MachinePictureLoader';
import { StyledMachineRobotNameBanner } from './MachineRobotNameBanner.styles';
import { MachineRobotConnectionStatusIndicator } from './MachineRobotConnectionStatusIndicator/MachineRobotConnectionStatusIndicator';
import { PrimaryButton } from 'lib/components/Button/PrimaryButton/PrimaryButton';
import { InfoTooltip } from 'lib/components/Tooltip/InfoTooltip/InfoTooltip';
import { MachineConnectionStatus } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import {
  LOCAL_STORAGE_VNC_KEY,
  LOCAL_STORAGE_VNC_ROBOT_SOFTWARE_VERSION_KEY,
  LOCAL_STORAGE_VNC_SERIAL_NUMBER,
} from 'config/constants';
import { InfoTooltipGlobalStyles } from 'lib/components/Tooltip/InfoTooltip/InfoTooltip.global.styles';
import { VncModalActions } from 'app/components/Vnc/modals/state/vncModalSlice';
import { VncSelectors } from 'app/components/Vnc/state/vncSelectors';
import { VncActions } from 'app/components/Vnc/state/vncSlice';
import { VncUtils } from 'app/components/Vnc/util/VncUtils';
import { VncWarningOneIsOpenAlreadyModal } from 'app/components/Vnc/modals/components/VncWarningOneIsOpenAlreadyModal/VncWarningOneIsOpenAlreadyModal';
import { VncModalSelectors } from 'app/components/Vnc/modals/state/vncModalSelectors';

interface MachineRobotNameBannerProps {
  robot: Machine;
}

export const MachineRobotNameBanner = ({ robot }: MachineRobotNameBannerProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const machineIdSelectorV2 = useSelector(VncSelectors.selectVncMachineIdV2);
  const vncToken = useSelector(VncSelectors.selectVncToken) || '';

  const isOneVncOpen = useMemo(() => {
    if (!localStorage.getItem(LOCAL_STORAGE_VNC_ROBOT_SOFTWARE_VERSION_KEY)) {
      return false;
    }

    return vncToken;
  }, [vncToken]);

  const isVncWarningOneIsOpenAlreadyModalVisible = useSelector(
    VncModalSelectors.selectVncModalWarningOneIsOpenAlreadyIsVisible
  );

  const isRemoteControlAvailable = useMemo(
    () => VncUtils.isUsingVncV2Solution(robot?.softwareVersion),
    [robot?.softwareVersion]
  );

  const handleEffectiveRequestVncSession = useCallback(
    () => dispatch(VncActions.vncRequestUrlV2Request({ machineId: robot.id, softwareVersion: robot?.softwareVersion })),
    [dispatch, robot.id, robot?.softwareVersion]
  );

  /**
   * @context user click "Remote Control" button
   * @description Handle access VNC session when clicking on "Access remote control"
   * @usage when user proactively click the button to access VNC session
   */
  const handleRequestVncSession = useCallback(() => {
    if (isRemoteControlAvailable) {
      if (!isOneVncOpen && robot.id) {
        dispatch(VncActions.vncOpenWindow());
        handleEffectiveRequestVncSession();
        localStorage.setItem(LOCAL_STORAGE_VNC_KEY, robot.id);
        localStorage.setItem(LOCAL_STORAGE_VNC_SERIAL_NUMBER, robot.serialNumber);
        localStorage.setItem(LOCAL_STORAGE_VNC_ROBOT_SOFTWARE_VERSION_KEY, robot?.softwareVersion || '');
      } else {
        dispatch(
          VncModalActions.vncModalWarningOneIsOpenAlreadyShow({
            machineId: robot.id,
            softwareVersion: robot?.softwareVersion,
            serialNumber: robot.serialNumber,
          })
        );
      }
    }
  }, [
    dispatch,
    robot.id,
    robot.serialNumber,
    robot?.softwareVersion,
    isRemoteControlAvailable,
    handleEffectiveRequestVncSession,
    isOneVncOpen,
  ]);

  return (
    <StyledMachineRobotNameBanner className="machine-robot-name-banner">
      <InfoTooltipGlobalStyles />
      <div className="machine-robot-name-banner__image">
        {robot.variant?.picture.product ? (
          <Image src={robot.variant?.picture.product} width={140} height={140} preview={false} />
        ) : (
          <MachinePictureLoader className="machine-robot-name-banner__image--loading" />
        )}
      </div>
      <div className="machine-robot-name-banner__info">
        <MachineRobotConnectionStatusIndicator
          lastActivityAt={robot?.lastActivityAt}
          connectionStatus={robot?.connectionStatus}
        />
        <div className="machine-robot-name-banner__name">{robot.name}</div>
        <div className="machine-robot-name-banner__type">{robot.type?.name}</div>
        {robot.id !== machineIdSelectorV2 && isRemoteControlAvailable && (
          <>
            <PrimaryButton
              size="m"
              key="request-button"
              className="machine-robot-name-banner__remote-control-button"
              onClick={handleRequestVncSession}
              disabled={robot.connectionStatus !== MachineConnectionStatus.Online}
              icon={
                robot.connectionStatus !== MachineConnectionStatus.Online && (
                  <Tooltip
                    className="tooltip"
                    title={<InfoTooltip content={t(`machineDetails.tooltip.vncButtonDisabled`)} />}
                    overlayClassName="tooltip-overlay"
                    placement="bottomLeft"
                  >
                    <div>
                      <SvgIcon name="info" className="machine-robot-name-banner__info-icon" />
                    </div>
                  </Tooltip>
                )
              }
            >
              <span className="machine-robot-name-banner__remote-control-button--label">{t('vnc.title')}</span>
            </PrimaryButton>
          </>
        )}
      </div>
      {isVncWarningOneIsOpenAlreadyModalVisible && <VncWarningOneIsOpenAlreadyModal />}
    </StyledMachineRobotNameBanner>
  );
};
